import {
  BoxGeometry,
  BufferGeometry,
  Color,
  CylinderGeometry,
  DodecahedronGeometry,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  PointLight,
  Quaternion,
  SphereGeometry,
  Vector3,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { createRandom } from '../../../utils/random.js';
import { addStaticCollider } from './util.js';
import {
  CITY_PALETTE,
  createGoSignTexture,
  createLinaaSignTexture,
  createShopSignTexture,
  createStreetSignTexture,
  createUTurnTexture,
} from './theme.js';
import { JUICE_SHOP_SITE } from './juiceShop.js';
import { CROSS_STREETS, MEDIAN_OPENINGS, ROAD, STATIONS } from './route.js';

const UP = new Vector3(0, 1, 0);

const SOURCES = {
  box: new BoxGeometry(1, 1, 1),
  cylinder: new CylinderGeometry(1, 1, 1, 9),
  dodeca: new DodecahedronGeometry(1, 0),
  sphere: new SphereGeometry(1, 12, 7),
  halfSphere: new SphereGeometry(1, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2),
};

const WRECK_BODY_COLORS = [
  '#583a2e', '#6c6152', '#743a30', '#3f4847', '#7a684d', '#4d4a43',
  '#2e3333', '#665140',
];
const WRECK_GLASS = '#263238';
const WRECK_DARK = '#222421';
const WRECK_RUBBER = '#181a19';
const WRECK_SCORCH = '#171512';
const WHEEL_QUAT = new Quaternion().setFromAxisAngle(
  new Vector3(0, 0, 1),
  Math.PI / 2,
);
const IDENTITY_QUAT = new Quaternion();
const WHEEL_LOCAL_POSITIONS = [
  [-0.84, 0.33, 1.25],
  [0.84, 0.33, 1.25],
  [-0.84, 0.33, -1.25],
  [0.84, 0.33, -1.25],
];

const SHOP_NAMES = [
  { text: 'بقالة الأمل', background: '#e7d8bb', foreground: '#2b352d', accent: '#2f6f4f' },
  { text: 'ورشة ميكانيكا', background: '#d7d2c4', foreground: '#273139', accent: '#b54732' },
  { text: 'شاي وقهوة', background: '#f1e3c8', foreground: '#3c2a1f', accent: '#c57f2c' },
  { text: 'مخبز الحي', background: '#ecdcc4', foreground: '#3d2d20', accent: '#b5653b' },
  { text: 'صيدلية النيل', background: '#eef1e8', foreground: '#1f4b4d', accent: '#2f8781' },
  { text: 'قطع غيار', background: '#d8d3c7', foreground: '#303237', accent: '#4f5d66' },
  { text: 'مطعم الشارع', background: '#e9d5b8', foreground: '#432a1e', accent: '#a3442f' },
  { text: 'حلاق', background: '#d9e0df', foreground: '#22343b', accent: '#3a768e' },
];

export function buildCity(level, route, materials) {
  const random = createRandom(9137);
  const chunks = new Map();
  const ctx = {
    level,
    route,
    materials,
    random,
    chunks,
    wreckRandom: createRandom(60817),
    wreckPiles: 0,
    wreckCars: 0,
    // Every wall segment, so the graffiti pass can hang murals on real walls
    // instead of guessing at their setback and height.
    walls: [],
    // Street-lamp head world positions (for the night light pool) and the
    // facade windows that glow after dark.
    lampHeads: [],
    windows: [],
  };
  const cityMaterial = new MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.94,
    metalness: 0.015,
  });

  const shopSignMaterials = SHOP_NAMES.map((entry) => ({
    material: new MeshBasicMaterial({
      map: createShopSignTexture(entry.text, entry),
      toneMapped: false,
    }),
  }));
  ctx.shopSignMaterials = shopSignMaterials;

  const ranges = buildBlockRanges(route);
  const exclusions = [
    ...STATIONS.map((station) => ({
      side: 1,
      start: station.s - 60,
      end: station.s + 60,
    })),
    { side: -1, start: 2850, end: 3070 }, // mosque
    { side: 1, start: 4500, end: 4730 }, // school
    { side: -1, start: 1486, end: 1504 }, // GO tower
    { side: -1, start: 3166, end: 3201 }, // LINAA tower
    {
      side: JUICE_SHOP_SITE.side,
      start: JUICE_SHOP_SITE.exclusionStart,
      end: JUICE_SHOP_SITE.exclusionEnd,
    }, // ٥٠ فاكهة juice shop
  ];

  let blockIndex = 0;
  for (const side of [-1, 1]) {
    for (const [rangeStart, rangeEnd] of ranges) {
      const pieces = splitRange(rangeStart, rangeEnd, exclusions.filter((e) => e.side === side));
      for (const [start, end] of pieces) {
        if (end - start < 24) continue;
        buildBlock(ctx, side, start, end, blockIndex);
        blockIndex += 1;
      }
    }
  }

  // Cross streets are the widest breaks in the roadside compound walls.
  // Close each mouth with a pile of wrecks on both sides of Street 60.
  for (const s of CROSS_STREETS) {
    for (const side of [-1, 1]) {
      addWreckPile(ctx, side, s, side * 16.8, 'cross');
    }
  }

  buildMosque(ctx, shopSignMaterials[4]);
  buildSchool(ctx, shopSignMaterials[0]);
  buildGoTower(ctx, shopSignMaterials);
  buildLinaaTower(ctx, shopSignMaterials);
  buildStreetLights(ctx);
  buildPowerLines(ctx);
  buildStreetFurniture(ctx, shopSignMaterials);

  flushChunks(ctx, cityMaterial);

  const lamps = buildLampLighting(ctx);
  const windows = buildWindowGlow(ctx);

  return {
    wreckPiles: ctx.wreckPiles,
    wreckCars: ctx.wreckCars,
    walls: ctx.walls,
    lamps,
    windows,
  };
}

/**
 * Real illumination for the existing street-light poles. One emissive InstancedMesh
 * gives every head a warm glow cap; a small pool of PointLights is re-pointed each
 * frame at the heads nearest the player so the street is lit by moving pools of
 * light rather than by hundreds of fixed lamps (which would blow the light budget).
 */
function buildLampLighting(ctx) {
  const heads = ctx.lampHeads;
  if (heads.length === 0) return { update() {} };

  const capGeo = new BoxGeometry(0.62, 0.18, 0.34);
  const capMat = new MeshStandardMaterial({
    color: 0x2a2622,
    emissive: 0xffcf8a,
    emissiveIntensity: 0,
    roughness: 0.6,
    metalness: 0.1,
  });
  const caps = new InstancedMesh(capGeo, capMat, heads.length);
  caps.frustumCulled = false;
  const dummy = new Object3D();
  heads.forEach((h, i) => {
    dummy.position.set(h.x, h.y, h.z);
    dummy.rotation.set(0, h.yaw, 0);
    dummy.updateMatrix();
    caps.setMatrixAt(i, dummy.matrix);
  });
  caps.instanceMatrix.needsUpdate = true;
  ctx.level.root.add(caps);

  const POOL = 14;
  const lights = [];
  for (let i = 0; i < POOL; i += 1) {
    const pl = new PointLight(0xffcf8a, 0, 30, 2);
    pl.position.set(0, -1000, 0); // parked off-scene until the first update
    ctx.level.root.add(pl);
    lights.push(pl);
  }

  const indices = heads.map((_, i) => i);

  return {
    mesh: caps,
    material: capMat,
    update(focus, nightFactor) {
      capMat.emissiveIntensity = nightFactor * 1.6;
      if (nightFactor <= 0.02) {
        for (const pl of lights) pl.intensity = 0;
        return;
      }
      const ranked = indices
        .map((i) => {
          const h = heads[i];
          const dx = h.x - focus.x;
          const dz = h.z - focus.z;
          return { i, d2: dx * dx + dz * dz };
        })
        .sort((a, b) => a.d2 - b.d2);
      for (let k = 0; k < POOL; k += 1) {
        const pl = lights[k];
        if (k < ranked.length) {
          const h = heads[ranked[k].i];
          pl.position.set(h.x, h.y, h.z);
          pl.intensity = nightFactor * 16;
        } else {
          pl.intensity = 0;
        }
      }
    },
  };
}

/**
 * One emissive InstancedMesh of warm window glows, placed on the real building
 * facades collected during the block build. Dark by day, glowing after dark.
 */
function buildWindowGlow(ctx) {
  const wins = ctx.windows;
  if (wins.length === 0) return { mesh: null, material: null };

  const geo = new PlaneGeometry(1, 1);
  const mat = new MeshStandardMaterial({
    color: 0x0c0f14,
    emissive: 0xffd9a0,
    emissiveIntensity: 0,
    roughness: 1,
    metalness: 0,
    side: DoubleSide,
    fog: true,
  });
  const mesh = new InstancedMesh(geo, mat, wins.length);
  mesh.frustumCulled = false;
  const dummy = new Object3D();
  wins.forEach((w, i) => {
    dummy.position.set(w.x, w.y, w.z);
    dummy.rotation.set(0, w.yaw, 0);
    dummy.scale.set(w.w, w.h, 1);
    dummy.updateMatrix();
    mesh.setMatrixAt(i, dummy.matrix);
  });
  mesh.instanceMatrix.needsUpdate = true;
  ctx.level.root.add(mesh);

  return { mesh, material: mat };
}

function buildBlockRanges(route) {
  const ranges = [];
  let cursor = 58;
  for (const s of CROSS_STREETS) {
    const end = s - 6;
    if (end - cursor > 24) ranges.push([cursor, end]);
    cursor = s + 6;
  }
  if (route.length - 44 - cursor > 24) ranges.push([cursor, route.length - 44]);
  return ranges;
}

function splitRange(start, end, exclusions) {
  const blockers = exclusions
    .filter((entry) => entry.end > start && entry.start < end)
    .sort((a, b) => a.start - b.start);
  const pieces = [];
  let cursor = start;
  for (const blocker of blockers) {
    if (blocker.start > cursor) pieces.push([cursor, Math.min(blocker.start, end)]);
    cursor = Math.max(cursor, blocker.end);
  }
  if (cursor < end) pieces.push([cursor, end]);
  return pieces;
}

function buildBlock(ctx, side, start, end, blockIndex) {
  const { random, route } = ctx;
  const length = end - start;
  const setback = side > 0 ? random.range(19.5, 26.5) : random.range(18.5, 25);
  const wallColor = random.pick(CITY_PALETTE.wall);
  const trimColor = random.pick(CITY_PALETTE.wallTrim);
  const wallHeight = random.range(2.2, 3.1);

  const gateAllowed = length > 55;
  const gate = gateAllowed && random() < 0.72
    ? random.range(start + 16, end - 16)
    : null;
  const gateHalf = 1.8;

  if (gate) {
    addWallRun(ctx, side, start + 1, gate - gateHalf, setback, wallHeight, wallColor, trimColor);
    addWallRun(ctx, side, gate + gateHalf, end - 1, setback, wallHeight, wallColor, trimColor);
    addGate(ctx, side, gate, setback, wallHeight, trimColor);
    addWreckPile(ctx, side, gate, side * setback, 'gate');
  } else {
    addWallRun(ctx, side, start + 1, end - 1, setback, wallHeight, wallColor, trimColor);
  }

  const facadeColor = random.pick(CITY_PALETTE.house);
  const houseCount = Math.max(1, Math.min(4, Math.floor(length / 48)));
  const houseSpan = length / houseCount;
  for (let i = 0; i < houseCount; i += 1) {
    const houseS = start + houseSpan * (i + 0.5) + random.range(-6, 6);
    const lateral = side * (setback + random.range(7, 20));
    const width = random.range(8, 14);
    const height = random.range(3.2, 6.8);
    addHouse(ctx, {
      s: houseS,
      lateral,
      width,
      length: random.range(9, 17),
      height,
      yaw: random.range(-0.04, 0.04),
      color: random() < 0.35 ? random.pick(CITY_PALETTE.house) : facadeColor,
      side,
      detailed: true,
    });
  }

  // Dense urban backdrop behind the plot walls.
  for (let i = 0; i < 4; i += 1) {
    addHouse(ctx, {
      s: random.range(start, end),
      lateral: side * random.range(62, 125),
      width: random.range(10, 24),
      length: random.range(12, 32),
      height: random.range(2.8, 6.6),
      yaw: random.range(-0.12, 0.12),
      color: random.pick(CITY_PALETTE.house),
      side,
      detailed: false,
    });
  }

  for (let i = 0; i < 3; i += 1) {
    addTree(ctx, {
      s: random.range(start + 8, end - 8),
      lateral: side * (setback + random.range(2, 24)),
      scale: random.range(0.85, 1.5),
    });
  }

  // A street tree every second block or so.
  if (random() < 0.62) {
    addTree(ctx, {
      s: random.range(start + 16, end - 16),
      lateral: side * 13.8,
      scale: random.range(0.8, 1.2),
    });
  }

  // Shops facing the pavement, tucked in front of the compound wall. Long
  // blocks get one near each cross street, which is how Street 60 actually
  // works: a run of walled houses interrupted by little commercial corners.
  if (length > 60) {
    const shopCount = length > 180 ? 2 : 1;
    for (let i = 0; i < shopCount; i += 1) {
      if (random() > (i === 0 ? 0.86 : 0.62)) continue;
      const shopS = i === 0
        ? start + random.range(12, 26)
        : end - random.range(12, 26);
      addShop(ctx, side, shopS, setback, blockIndex * 2 + i);
    }
  }
}

function addWallRun(ctx, side, start, end, setback, height, wallColor, trimColor) {
  const { route, random, chunks, level } = ctx;
  if (end - start < 2) return;
  const lateral = side * setback;
  const wallDepth = 0.3;
  const segment = 5.5;

  for (let s = start; s < end; s += segment) {
    const length = Math.min(segment + 0.15, end - s);
    const mid = s + length / 2;
    const frame = route.frameAt(mid);
    const position = frame.position.clone().addScaledVector(frame.right, lateral);
    addPart(ctx, side, mid, 'box', {
      position: new Vector3(position.x, height / 2, position.z),
      scale: new Vector3(wallDepth, height, length),
      yaw: frame.yaw,
      color: wallColor,
    });
    addPart(ctx, side, mid, 'box', {
      position: new Vector3(position.x, height + 0.045, position.z),
      scale: new Vector3(wallDepth + 0.16, 0.09, length + 0.06),
      yaw: frame.yaw,
      color: trimColor,
    });
    ctx.walls.push({ side, s: mid, lateral, height, length });
  }

  // Physics wall colliders in short straight chunks. They follow the road
  // closely enough without adding one body per visual segment.
  const colliderLength = 32;
  for (let s = start; s < end; s += colliderLength) {
    const length = Math.min(colliderLength + 0.2, end - s);
    const frame = route.offsetFrame(s + length / 2, lateral);
    addStaticCollider(level, {
      x: frame.position.x,
      y: height / 2,
      z: frame.position.z,
      width: wallDepth + 0.1,
      height,
      depth: length,
      yaw: frame.yaw,
    });
  }

  // Occasional low shrubs at the foot of the wall.
  if (random() < 0.4) {
    const s = random.range(start, end);
    const frame = route.offsetFrame(s, lateral + side * -0.8);
    addPart(ctx, side, s, 'dodeca', {
      position: new Vector3(frame.position.x, 0.55, frame.position.z),
      scale: new Vector3(0.7, 0.55, 0.8),
      yaw: random() * Math.PI,
      color: CITY_PALETTE.dryGreen,
    });
  }
}

function addGate(ctx, side, s, setback, wallHeight, trimColor) {
  const { route, level } = ctx;
  const lateral = side * setback;
  const frame = route.offsetFrame(s, lateral);
  addPart(ctx, side, s, 'box', {
    position: new Vector3(frame.position.x, 0.9, frame.position.z),
    scale: new Vector3(0.16, 1.8, 3.8),
    yaw: frame.yaw,
    color: '#2c3d45',
  });
  for (const direction of [-1, 1]) {
    const post = route.offsetFrame(s + direction * 1.95, lateral);
    addPart(ctx, side, s, 'box', {
      position: new Vector3(post.position.x, wallHeight / 2 + 0.15, post.position.z),
      scale: new Vector3(0.55, wallHeight + 0.3, 0.45),
      yaw: post.yaw,
      color: trimColor,
    });
  }
  addStaticCollider(level, {
    x: frame.position.x,
    y: 1.1,
    z: frame.position.z,
    width: 0.18,
    height: 2.2,
    depth: 3.8,
    yaw: frame.yaw,
  });
}

function addHouse(ctx, { s, lateral, width, length, height, yaw, color, side, detailed }) {
  const { route, random } = ctx;
  const frame = route.frameAt(s);
  const position = frame.position.clone().addScaledVector(frame.right, lateral);
  const rotation = frame.yaw + yaw;

  addPart(ctx, side, s, 'box', {
    position: new Vector3(position.x, height / 2, position.z),
    scale: new Vector3(width, height, length),
    yaw: rotation,
    color,
  });

  // Flat roof lip and a small parapet on the street elevation.
  addPart(ctx, side, s, 'box', {
    position: new Vector3(position.x, height + 0.1, position.z),
    scale: new Vector3(width + 0.38, 0.2, length + 0.38),
    yaw: rotation,
    color: random.pick(CITY_PALETTE.roof),
  });

  if (detailed) {
    const streetEdge = lateral - side * (width / 2 - 0.02);
    const facade = frame.position.clone().addScaledVector(frame.right, streetEdge);
    const windowColor = '#28363d';

    // Lit windows for the night pass: a small grid on the road-facing facade,
    // aligned to the real building so the glow sits on the wall, not in mid-air.
    const frontYaw = frame.yaw + (side > 0 ? -Math.PI / 2 : Math.PI / 2);
    const ox = -side * frame.right.x * 0.06;
    const oz = -side * frame.right.z * 0.06;
    const cols = 2;
    const rows = height > 5 ? 3 : 2;
    const winW = Math.min(width * 0.32, 1.4);
    const winH = Math.min(height * 0.16, 0.8);
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const along = (c - (cols - 1) / 2) * (width * 0.42);
        ctx.windows.push({
          x: facade.x + frame.tangent.x * along + ox,
          y: height * (0.3 + r * 0.24),
          z: facade.z + frame.tangent.z * along + oz,
          yaw: frontYaw,
          w: winW,
          h: winH,
        });
      }
    }

    if (height > 4.4 && random() < 0.8) {
      addPart(ctx, side, s, 'box', {
        position: new Vector3(facade.x, height * 0.66, facade.z),
        scale: new Vector3(0.08, 1.0, Math.min(2.6, length * 0.3)),
        yaw: rotation,
        color: windowColor,
      });
    }
    if (random() < 0.65) {
      addPart(ctx, side, s, 'box', {
        position: new Vector3(facade.x, height * 0.34, facade.z),
        scale: new Vector3(0.08, 1.6, 1.2),
        yaw: rotation,
        color: '#3a3028',
      });
    }

    if (random() < 0.5) {
      const tank = frame.position
        .clone()
        .addScaledVector(frame.right, lateral + random.range(-width * 0.25, width * 0.25));
      addPart(ctx, side, s, 'cylinder', {
        position: new Vector3(tank.x, height + 0.95, tank.z),
        scale: new Vector3(0.48, 1.35, 0.48),
        yaw: random() * Math.PI,
        color: random() < 0.45 ? '#d9d4c8' : '#333b3e',
      });
    }

    if (random() < 0.25) {
      const dish = frame.position
        .clone()
        .addScaledVector(frame.right, lateral + random.range(-width * 0.3, width * 0.3));
      addPart(ctx, side, s, 'cylinder', {
        position: new Vector3(dish.x, height + 0.35, dish.z),
        scale: new Vector3(0.38, 0.1, 0.38),
        yaw: random() * Math.PI,
        color: '#d4d1c8',
      });
    }
  }
}

function addShop(ctx, side, s, setback, blockIndex) {
  const { route, level, random } = ctx;
  const shopDepth = 4.8;
  const shopLength = random.range(7, 10);
  const lateral = side * (14.6 + shopDepth / 2);
  const frame = route.offsetFrame(s, lateral);
  const frontLateral = side * 14.55;
  const front = route.offsetFrame(s, frontLateral);
  const awningColor = random.pick(['#a54a36', '#2e6b56', '#b57e30', '#3f627f']);

  addPart(ctx, side, s, 'box', {
    position: new Vector3(frame.position.x, 1.65, frame.position.z),
    scale: new Vector3(shopDepth, 3.3, shopLength),
    yaw: frame.yaw,
    color: random.pick(CITY_PALETTE.house),
  });
  addPart(ctx, side, s, 'box', {
    position: new Vector3(frame.position.x, 3.36, frame.position.z),
    scale: new Vector3(shopDepth + 0.3, 0.14, shopLength + 0.24),
    yaw: frame.yaw,
    color: random.pick(CITY_PALETTE.roof),
  });
  addPart(ctx, side, s, 'box', {
    position: new Vector3(front.position.x, 2.55, front.position.z),
    scale: new Vector3(1.5, 0.1, shopLength * 0.82),
    yaw: frame.yaw,
    color: awningColor,
  });
  addPart(ctx, side, s, 'box', {
    position: new Vector3(front.position.x, 1.15, front.position.z),
    scale: new Vector3(0.08, 2.3, shopLength * 0.55),
    yaw: frame.yaw,
    color: '#1f2b30',
  });

  addStaticCollider(level, {
    x: frame.position.x,
    y: 1.65,
    z: frame.position.z,
    width: shopDepth,
    height: 3.3,
    depth: shopLength,
    yaw: frame.yaw,
  });

  const material = ctx.shopSignMaterials[blockIndex % ctx.shopSignMaterials.length].material;
  const sign = new Mesh(new PlaneGeometry(Math.min(4.4, shopLength * 0.72), 1.0), material);
  sign.position.set(front.position.x, 3.0, front.position.z);
  sign.rotation.y = frame.yaw + (side > 0 ? -Math.PI / 2 : Math.PI / 2);
  ctx.level.root.add(sign);

  // A lit shopfront window for the night pass.
  const frontYaw = frame.yaw + (side > 0 ? -Math.PI / 2 : Math.PI / 2);
  ctx.windows.push({
    x: front.position.x - side * frame.right.x * 0.06,
    y: 1.2,
    z: front.position.z - side * frame.right.z * 0.06,
    yaw: frontYaw,
    w: shopLength * 0.7,
    h: 1.4,
  });

  addSidewalkPerson(ctx, side, s + random.range(-shopLength * 0.32, shopLength * 0.1), random);
  if (random() < 0.65) {
    addSidewalkPerson(ctx, side, s + random.range(shopLength * 0.16, shopLength * 0.4), random);
  }

  // Crates, a bin and a plastic chair spill out of every second shop.
  for (let i = 0; i < 2; i += 1) {
    const crate = route.offsetFrame(s + random.range(-shopLength * 0.4, shopLength * 0.4), side * 13.5);
    addPart(ctx, side, s, 'box', {
      position: new Vector3(crate.position.x, 0.3 + i * 0.12, crate.position.z),
      scale: new Vector3(0.55, 0.55, 0.55),
      yaw: crate.yaw + random.range(-0.3, 0.3),
      color: i === 0 ? '#8e5b35' : '#3d5a67',
    });
  }
}

function addSidewalkPerson(ctx, side, s, random) {
  const { route } = ctx;
  const frame = route.offsetFrame(s, side * random.range(12.8, 13.6));
  const robe = random.pick(['#e7ddc9', '#c9d1c5', '#d9cbb3', '#f0ece2', '#aebfc0']);
  const skin = random.pick(['#704f37', '#8b6548', '#5e402b']);
  const height = random.range(1.55, 1.86);

  addPart(ctx, side, s, 'cylinder', {
    position: new Vector3(frame.position.x, height * 0.35, frame.position.z),
    scale: new Vector3(0.24, height * 0.7, 0.24),
    yaw: random() * Math.PI,
    color: robe,
  });
  addPart(ctx, side, s, 'sphere', {
    position: new Vector3(frame.position.x, height * 0.82, frame.position.z),
    scale: new Vector3(0.14, 0.16, 0.14),
    yaw: random() * Math.PI,
    color: skin,
  });
  addPart(ctx, side, s, 'cylinder', {
    position: new Vector3(frame.position.x, height * 0.9, frame.position.z),
    scale: new Vector3(0.16, 0.06, 0.16),
    yaw: random() * Math.PI,
    color: '#efe8d6',
  });
}

function addTree(ctx, { s, lateral, scale = 1 }) {
  const { route, random } = ctx;
  const frame = route.offsetFrame(s, lateral);
  const height = 2.5 * scale + random.range(0, 1.2);

  addPart(ctx, 1, s, 'cylinder', {
    position: new Vector3(frame.position.x, height / 2, frame.position.z),
    scale: new Vector3(0.16 * scale, height, 0.16 * scale),
    yaw: random() * Math.PI,
    color: '#5a4732',
  });

  const canopyColor = random() < 0.5 ? CITY_PALETTE.green : CITY_PALETTE.dustyGreen;
  addPart(ctx, 1, s, 'dodeca', {
    position: new Vector3(frame.position.x, height + 0.45, frame.position.z),
    scale: new Vector3(1.65 * scale, 0.95 * scale, 1.55 * scale),
    yaw: random() * Math.PI,
    color: canopyColor,
  });
  if (random() < 0.5) {
    addPart(ctx, 1, s + 0.6, 'dodeca', {
      position: new Vector3(frame.position.x, height + 0.8, frame.position.z - 0.3),
      scale: new Vector3(1.0 * scale, 0.7 * scale, 0.9 * scale),
      yaw: random() * Math.PI,
      color: CITY_PALETTE.dryGreen,
    });
  }
}

function buildStreetLights(ctx) {
  const { route, random } = ctx;
  for (let s = 24; s < route.length - 24; s += 58) {
    for (const side of [-1, 1]) {
      const lateral = side * 13.7;
      const lightS = s + random.range(-2, 2);
      // Keep the forecourt approach and pylon clear on the station side.
      if (side > 0 && STATIONS.some((station) => Math.abs(station.s - lightS) < 38)) {
        continue;
      }
      const frame = route.offsetFrame(lightS, lateral);
      addPart(ctx, side, lightS, 'cylinder', {
        position: new Vector3(frame.position.x, 4.1, frame.position.z),
        scale: new Vector3(0.1, 8.2, 0.1),
        color: '#5f6260',
      });
      const arm = route.offsetFrame(lightS, lateral - side * 1.15);
      addPart(ctx, side, lightS, 'box', {
        position: new Vector3(arm.position.x, 8.08, arm.position.z),
        scale: new Vector3(2.3, 0.1, 0.1),
        yaw: frame.yaw,
        color: '#5f6260',
      });
      const head = route.offsetFrame(lightS, lateral - side * 2.2);
      addPart(ctx, side, lightS, 'box', {
        position: new Vector3(head.position.x, 7.96, head.position.z),
        scale: new Vector3(0.62, 0.18, 0.34),
        yaw: frame.yaw,
        color: '#d7d4c4',
      });
      // Remember the head so the night pass can hang a real light + glow on it.
      ctx.lampHeads.push({ x: head.position.x, y: 7.96, z: head.position.z, yaw: frame.yaw });
    }
  }
}

function buildPowerLines(ctx) {
  const { route } = ctx;
  const wirePoints = [];
  for (let s = 42; s < route.length - 30; s += 72) {
    const frame = route.offsetFrame(s, -15.4);
    addPart(ctx, -1, s, 'cylinder', {
      position: new Vector3(frame.position.x, 4.3, frame.position.z),
      scale: new Vector3(0.12, 8.6, 0.12),
      color: '#817b6d',
    });
    addPart(ctx, -1, s, 'box', {
      position: new Vector3(frame.position.x, 7.9, frame.position.z),
      scale: new Vector3(2.4, 0.13, 0.13),
      yaw: frame.yaw,
      color: '#4f4d45',
    });
    wirePoints.push(frame);
  }

  if (wirePoints.length < 2) return;
  const positions = [];
  for (let i = 1; i < wirePoints.length; i += 1) {
    const a = wirePoints[i - 1];
    const b = wirePoints[i];
    for (const height of [7.75, 7.98, 8.18]) {
      for (const lateral of [-0.7, 0.7]) {
        const p1 = a.position.clone().addScaledVector(a.right, lateral).setY(height);
        const p2 = b.position.clone().addScaledVector(b.right, lateral).setY(height);
        positions.push(p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
      }
    }
  }
  const lineGeometry = new BufferGeometry();
  lineGeometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  const lines = new LineSegments(
    lineGeometry,
    new LineBasicMaterial({
      color: 0x3f3d38,
      transparent: true,
      opacity: 0.65,
      fog: true,
    }),
  );
  ctx.level.root.add(lines);
}

function buildStreetFurniture(ctx, shopSignMaterials) {
  const { route, level } = ctx;
  const streetTexture = createStreetSignTexture();
  const streetMaterial = new MeshBasicMaterial({ map: streetTexture, toneMapped: false });
  const streetBoardGeometry = new PlaneGeometry(4.8, 1.44);
  const postMaterial = new MeshStandardMaterial({
    color: 0x59605e,
    roughness: 0.6,
    metalness: 0.5,
  });
  const postGeometry = new BoxGeometry(0.09, 4.8, 0.09);

  for (const entry of [
    { s: 1090, side: 1, facing: 0 },
    { s: route.length - 90, side: -1, facing: Math.PI },
  ]) {
    const frame = route.offsetFrame(entry.s, entry.side * 13.9);
    const group = new Group();
    group.position.copy(frame.position);
    group.rotation.y = frame.yaw + entry.facing;
    const board = new Mesh(streetBoardGeometry, streetMaterial);
    board.position.y = 4.75;
    group.add(board);
    for (const offset of [-1.9, 1.9]) {
      const post = new Mesh(postGeometry, postMaterial);
      post.position.set(offset, 2.4, 0);
      group.add(post);
    }
    rootAdd(level, group);
  }

  const uTurnTexture = createUTurnTexture();
  const uTurnMaterial = new MeshBasicMaterial({ map: uTurnTexture, toneMapped: false });
  const uTurnGeometry = new PlaneGeometry(1.7, 1.7);
  const uPostGeometry = new BoxGeometry(0.08, 2.9, 0.08);

  for (const opening of MEDIAN_OPENINGS) {
    const entries = [
      { s: opening.s - opening.width / 2 - 10, side: 1, facing: 0 },
      { s: opening.s + opening.width / 2 + 10, side: -1, facing: Math.PI },
    ];
    for (const entry of entries) {
      const frame = route.offsetFrame(entry.s, entry.side * 13.75);
      const group = new Group();
      group.position.copy(frame.position);
      group.rotation.y = frame.yaw + entry.facing;
      const board = new Mesh(uTurnGeometry, uTurnMaterial);
      board.position.y = 2.55;
      group.add(board);
      const post = new Mesh(uPostGeometry, postMaterial);
      post.position.y = 1.45;
      group.add(post);
      rootAdd(level, group);
    }
  }

  // One bus shelter, because the long queue makes waiting a local sport.
  const shelterS = 1710;
  const shelterFrame = route.offsetFrame(shelterS, -14.7);
  const shelterGroup = new Group();
  shelterGroup.position.copy(shelterFrame.position);
  shelterGroup.rotation.y = shelterFrame.yaw;
  const shelterRoof = new Mesh(
    new BoxGeometry(2.6, 0.12, 5.2),
    new MeshStandardMaterial({ color: 0x9ea49e, roughness: 0.65, metalness: 0.35 }),
  );
  shelterRoof.position.y = 2.7;
  shelterRoof.castShadow = true;
  shelterGroup.add(shelterRoof);
  for (const z of [-2.2, 2.2]) {
    for (const x of [-1.0, 1.0]) {
      const post = new Mesh(
        new BoxGeometry(0.08, 2.7, 0.08),
        postMaterial,
      );
      post.position.set(x, 1.35, z);
      shelterGroup.add(post);
    }
  }
  const bench = new Mesh(
    new BoxGeometry(0.5, 0.1, 3.8),
    new MeshStandardMaterial({ color: 0x7d6a50, roughness: 0.9 }),
  );
  bench.position.set(-0.75, 0.55, 0);
  bench.castShadow = true;
  shelterGroup.add(bench);
  rootAdd(level, shelterGroup);

  // A couple of kiosks by the roadside.
  for (const entry of [
    { s: 870, side: 1 },
    { s: 3480, side: -1 },
  ]) {
    const frame = route.offsetFrame(entry.s, entry.side * 15.2);
    const group = new Group();
    group.position.copy(frame.position);
    group.rotation.y = frame.yaw;
    const body = new Mesh(
      new BoxGeometry(3.0, 2.6, 4.4),
      new MeshStandardMaterial({ color: 0xc6a96f, roughness: 0.9 }),
    );
    body.position.y = 1.3;
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);
    const roof = new Mesh(
      new BoxGeometry(3.5, 0.1, 4.9),
      new MeshStandardMaterial({ color: 0x7c6a4c, roughness: 0.9 }),
    );
    roof.position.y = 2.68;
    roof.castShadow = true;
    group.add(roof);
    const signMaterial = shopSignMaterials[blockModulo(entry.s, shopSignMaterials.length)].material;
    const sign = new Mesh(new PlaneGeometry(2.7, 0.8), signMaterial);
    sign.position.set(entry.side > 0 ? -1.52 : 1.52, 2.15, 0);
    sign.rotation.y = entry.side > 0 ? -Math.PI / 2 : Math.PI / 2;
    group.add(sign);
    rootAdd(level, group);
  }
}

function buildMosque(ctx, signEntry) {
  const { route, level, random } = ctx;
  const frame = route.offsetFrame(2960, -20);
  const wallColor = '#d9cdb6';
  const trimColor = '#48705b';

  addWallRun(ctx, -1, 2865, 2948, 21, 2.5, wallColor, trimColor);
  addWallRun(ctx, -1, 2972, 3055, 21, 2.5, wallColor, trimColor);
  addGate(ctx, -1, 2960, 21, 2.5, trimColor);
  addWreckPile(ctx, -1, 2960, -21, 'gate');

  const hall = route.offsetFrame(2960, -37);
  addPart(ctx, -1, 2960, 'box', {
    position: new Vector3(hall.position.x, 3.2, hall.position.z),
    scale: new Vector3(20, 6.4, 29),
    yaw: hall.yaw,
    color: '#e1d8c4',
  });
  addPart(ctx, -1, 2960, 'box', {
    position: new Vector3(hall.position.x, 6.55, hall.position.z),
    scale: new Vector3(20.5, 0.3, 29.5),
    yaw: hall.yaw,
    color: '#4e755f',
  });
  addPart(ctx, -1, 2960, 'cylinder', {
    position: new Vector3(hall.position.x, 7.1, hall.position.z),
    scale: new Vector3(5.3, 1.2, 5.3),
    color: '#e9e1d0',
  });
  addPart(ctx, -1, 2960, 'halfSphere', {
    position: new Vector3(hall.position.x, 7.7, hall.position.z),
    scale: new Vector3(5.5, 4.4, 5.5),
    color: '#a9b59d',
  });

  const minaret = route.offsetFrame(2995, -23.5);
  addPart(ctx, -1, 2995, 'box', {
    position: new Vector3(minaret.position.x, 7.5, minaret.position.z),
    scale: new Vector3(2.8, 15, 2.8),
    yaw: minaret.yaw,
    color: '#e5dcc9',
  });
  addPart(ctx, -1, 2995, 'box', {
    position: new Vector3(minaret.position.x, 14.8, minaret.position.z),
    scale: new Vector3(3.5, 0.3, 3.5),
    yaw: minaret.yaw,
    color: '#4e755f',
  });
  addPart(ctx, -1, 2995, 'cylinder', {
    position: new Vector3(minaret.position.x, 16.1, minaret.position.z),
    scale: new Vector3(1.8, 2.2, 1.8),
    color: '#dfe5d3',
  });
  addPart(ctx, -1, 2995, 'halfSphere', {
    position: new Vector3(minaret.position.x, 17.1, minaret.position.z),
    scale: new Vector3(1.9, 1.2, 1.9),
    color: '#4e755f',
  });

  addStaticCollider(level, {
    x: hall.position.x,
    y: 3.2,
    z: hall.position.z,
    width: 20,
    height: 6.4,
    depth: 29,
    yaw: hall.yaw,
  });
  addStaticCollider(level, {
    x: minaret.position.x,
    y: 7.5,
    z: minaret.position.z,
    width: 2.8,
    height: 15,
    depth: 2.8,
    yaw: minaret.yaw,
  });

  const signMaterial = new MeshBasicMaterial({ map: createShopSignTexture('مسجد الحي', signEntry), toneMapped: false });
  const sign = new Mesh(new PlaneGeometry(3.4, 0.9), signMaterial);
  const signFrame = route.offsetFrame(2960, -20.1);
  sign.position.set(signFrame.position.x, 2.2, signFrame.position.z);
  sign.rotation.y = signFrame.yaw + Math.PI / 2;
  level.root.add(sign);

  for (let i = 0; i < 4; i += 1) {
    addTree(ctx, {
      s: random.range(2880, 3040),
      lateral: -random.range(25, 45),
      scale: random.range(0.8, 1.4),
    });
  }
}

function buildSchool(ctx, signEntry) {
  const { route, level, random } = ctx;
  addWallRun(ctx, 1, 4510, 4530, 21, 2.4, '#c6b391', '#8d7659');
  addWallRun(ctx, 1, 4550, 4710, 21, 2.4, '#c6b391', '#8d7659');
  addGate(ctx, 1, 4540, 21, 2.4, '#8d7659');
  addWreckPile(ctx, 1, 4540, 21, 'gate');

  const hall = route.offsetFrame(4625, 38);
  addPart(ctx, 1, 4625, 'box', {
    position: new Vector3(hall.position.x, 3.5, hall.position.z),
    scale: new Vector3(18, 7, 46),
    yaw: hall.yaw,
    color: '#d6c5a7',
  });
  addPart(ctx, 1, 4625, 'box', {
    position: new Vector3(hall.position.x, 7.1, hall.position.z),
    scale: new Vector3(18.5, 0.25, 46.5),
    yaw: hall.yaw,
    color: '#987f5f',
  });
  for (let i = 0; i < 4; i += 1) {
    const s = 4590 + i * 12;
    const frame = route.offsetFrame(s, 29.02);
    addPart(ctx, 1, s, 'box', {
      position: new Vector3(frame.position.x, 3.0, frame.position.z),
      scale: new Vector3(0.08, 1.1, 2.0),
      yaw: frame.yaw,
      color: '#314047',
    });
    addPart(ctx, 1, s, 'box', {
      position: new Vector3(frame.position.x, 5.5, frame.position.z),
      scale: new Vector3(0.08, 1.0, 2.0),
      yaw: frame.yaw,
      color: '#314047',
    });
  }

  addStaticCollider(level, {
    x: hall.position.x,
    y: 3.5,
    z: hall.position.z,
    width: 18,
    height: 7,
    depth: 46,
    yaw: hall.yaw,
  });

  const sign = new Mesh(
    new PlaneGeometry(4.0, 1.0),
    new MeshBasicMaterial({ map: createShopSignTexture('مدرسة الحي', signEntry), toneMapped: false }),
  );
  const signFrame = route.offsetFrame(4540, 20.9);
  sign.position.set(signFrame.position.x, 2.65, signFrame.position.z);
  sign.rotation.y = signFrame.yaw - Math.PI / 2;
  level.root.add(sign);

  for (let i = 0; i < 3; i += 1) {
    addTree(ctx, {
      s: random.range(4560, 4700),
      lateral: random.range(24, 34),
      scale: random.range(0.8, 1.2),
    });
  }
}

/**
 * The GO tower: eight storeys of white panel and blue glazing with the
 * company's lightbox on the roof, standing where Street 60 turns commercial in
 * Al-Mamura. Modelled from a photograph of the junction — four glazed bays
 * between white pilasters, a shopfront podium opening onto the pavement,
 * balconies down the south end, and the sign on a steel frame above the
 * parapet. It replaces the compound wall for its frontage, the way the mosque
 * and the school already do.
 */
function buildGoTower(ctx, signMaterials) {
  const { route, level } = ctx;
  const S = 1495;

  const FRONTAGE = 17.4; // along the street
  const DEPTH = 13; // back from the pavement
  const GROUND = 4.4; // shopfront storey
  const FLOORS = 7; // seven above the shops, so eight storeys in all
  const FLOOR = 3.0;
  const BODY = GROUND + FLOORS * FLOOR;
  const PARAPET = 0.9;

  const SHELL = '#d9d3c6';
  const PANEL = '#e8e3d9';
  const GLASS = '#4a7a92';
  const REVEAL = '#2b3a41';
  const COPING = '#494540';
  const SHOP = '#2f3a3c';

  // The front face sits just behind the 3.2 m pavement, so the shops open onto
  // it the way they do in the photograph rather than sitting behind a wall.
  const frontLateral = 14.4;
  const frame = route.offsetFrame(S, -(frontLateral + DEPTH / 2));
  const { right, tangent, yaw } = frame;
  const at = (along, lateral, y) =>
    new Vector3(
      frame.position.x + tangent.x * along + right.x * lateral,
      y,
      frame.position.z + tangent.z * along + right.z * lateral,
    );
  // Boxes are authored with X across the street and Z along it, matching the
  // frame the route hands back.
  const slab = (along, lateral, y0, latW, h, alongL, color) =>
    addPart(ctx, -1, S, 'box', {
      position: at(along, lateral, y0 + h / 2),
      scale: new Vector3(latW, h, alongL),
      yaw,
      color,
    });

  const half = FRONTAGE / 2;
  const face = DEPTH / 2;

  /* ---- Massing and the shopfront podium ------------------------------ */

  slab(0, 0, 0, DEPTH, BODY, FRONTAGE, SHELL);

  // The shops step out a little, so the glazing catches the light.
  slab(0, 0.3, 0, DEPTH + 0.6, GROUND, FRONTAGE + 0.6, SHELL);

  // Mullioned shopfront glazing. A single black slab reads as a hole, so it is
  // broken into seven lights with pale mullions between them.
  const PODIUM = FRONTAGE + 0.2;
  const panels = 7;
  const mull = 0.28;
  const panelW = (PODIUM - (panels - 1) * mull) / panels;
  for (let i = 0; i < panels; i += 1) {
    const along = -PODIUM / 2 + panelW / 2 + i * (panelW + mull);
    slab(along, face + 0.66, 0.5, 0.1, 2.6, panelW, SHOP);
  }
  // Fascia the shop boards sit on.
  slab(0, face + 0.68, 3.2, 0.2, 1.15, PODIUM, '#c9c2b4');

  /* ---- Street facade -------------------------------------------------
     The real facade is a white field with vertical glazed strips and paired
     punched windows, not a blue curtain wall: the glazing runs as two narrow
     full-height bands, and everything else is cream panel with small dark
     openings in pairs. */

  // Cream skin over the massing so the field reads warm rather than grey.
  slab(0, face + 0.03, GROUND, 0.06, BODY - GROUND, FRONTAGE, PANEL);

  const strips = [
    { along: -3.0, width: 2.6 },
    { along: 6.7, width: 1.8 },
  ];
  for (const { along, width } of strips) {
    slab(along, face + 0.1, GROUND, 0.1, BODY - GROUND, width, GLASS);
    // Floor lines and a centre mullion, so the band reads as glazing.
    for (let f = 0; f < FLOORS; f += 1) {
      slab(along, face + 0.13, GROUND + f * FLOOR, 0.06, 0.3, width, PANEL);
    }
    slab(along, face + 0.13, GROUND, 0.06, BODY - GROUND, 0.1, '#33596b');
  }

  // Paired punched windows across the remaining field.
  const windowPairs = [-6.4, 0.2, 3.4];
  for (let f = 0; f < FLOORS; f += 1) {
    const base = GROUND + f * FLOOR;
    for (const along of windowPairs) {
      for (const offset of [-0.48, 0.48]) {
        slab(along + offset, face + 0.07, base + 0.9, 0.06, 1.25, 0.62, REVEAL);
      }
      slab(along, face + 0.06, base + 0.78, 0.05, 0.1, 1.7, PANEL);
    }
  }

  /* ---- Balconies stacked up the south end ----------------------------- */

  const balconyAlong = -6.5;
  for (let f = 0; f < FLOORS; f += 1) {
    const base = GROUND + f * FLOOR;
    slab(balconyAlong, face + 0.06, base + 0.3, 0.08, 2.15, 1.6, REVEAL); // door
    slab(balconyAlong, face + 0.75, base + 0.1, 1.5, 0.2, 2.8, PANEL); // deck
    slab(balconyAlong, face + 1.44, base + 0.3, 0.1, 1.0, 2.8, PANEL); // rail
    for (const along of [balconyAlong - 1.35, balconyAlong + 1.35]) {
      slab(along, face + 0.75, base + 0.3, 1.5, 1.0, 0.1, PANEL); // returns
    }
  }

  // Windows on the north end, in the same paired pattern as the front.
  for (let f = 1; f < FLOORS; f += 1) {
    const base = GROUND + f * FLOOR;
    for (const lateral of [-3.0, 1.4]) {
      for (const offset of [-0.48, 0.48]) {
        slab(half + 0.05, lateral + offset, base + 0.9, 0.62, 1.25, 0.08, REVEAL);
      }
      slab(half + 0.04, lateral, base + 0.78, 1.7, 0.1, 0.05, PANEL);
    }
  }

  /* ---- South elevation ------------------------------------------------
     This is the face seen head-on coming up from Bashair, so it carries the
     same language as the street front instead of reading as a white slab. */

  for (let f = 0; f < FLOORS; f += 1) {
    const base = GROUND + f * FLOOR;
    for (const lateral of [-2.4, 1.8]) {
      for (const offset of [-0.48, 0.48]) {
        slab(-half - 0.05, lateral + offset, base + 0.9, 0.62, 1.25, 0.08, REVEAL);
      }
      slab(-half - 0.04, lateral, base + 0.78, 1.7, 0.1, 0.05, PANEL);
    }
  }
  slab(-half - 0.06, -5.6, GROUND, 1.7, BODY - GROUND, 0.1, GLASS);
  for (let f = 0; f < FLOORS; f += 1) {
    slab(-half - 0.09, -5.6, GROUND + f * FLOOR, 1.7, 0.3, 0.06, PANEL);
  }
  slab(-half - 0.09, -5.6, GROUND, 0.1, BODY - GROUND, 0.06, '#33596b');

  /* ---- Roof, parapet and the sign ------------------------------------- */

  slab(0, 0, BODY, DEPTH + 0.5, PARAPET, FRONTAGE + 0.5, SHELL);
  slab(0, 0, BODY + PARAPET, DEPTH + 0.62, 0.14, FRONTAGE + 0.62, COPING);
  slab(-2.6, -2.2, BODY, 3.4, 2.2, 4.2, SHELL);

  // The lightbox sits forward on the roof, over the front parapet, so it reads
  // from the street rather than from behind the roof overrun. In the photo the
  // box is tall — about a fifth of the building — and carries the green
  // double-ring monogram, so it is sized for the logo rather than for text.
  const signBase = BODY + PARAPET;
  const signLateral = 5.4;
  const signRise = 1.4;
  const signHeight = 5.6;
  const signWidth = 13.0;
  for (const along of [-5.4, 5.4]) {
    for (const lateral of [signLateral - 0.22, signLateral + 0.22]) {
      slab(along, lateral, signBase, 0.2, signRise, 0.2, COPING);
    }
  }
  slab(0, signLateral, signBase + signRise, 0.55, signHeight, signWidth, '#eef1ea');

  const goSign = new Mesh(
    new PlaneGeometry(signWidth - 0.4, signHeight - 0.44),
    new MeshBasicMaterial({ map: createGoSignTexture(), toneMapped: false }),
  );
  goSign.name = 'go-sign';
  const goAt = at(0, signLateral + 0.3, signBase + signRise + signHeight / 2);
  goSign.position.set(goAt.x, goAt.y, goAt.z);
  goSign.rotation.y = yaw + Math.PI / 2;
  level.root.add(goSign);

  // Shop signs across the fascia, in the same painted-board language as the
  // rest of the street.
  for (let i = 0; i < 3; i += 1) {
    const sign = new Mesh(
      new PlaneGeometry(5.2, 0.78),
      signMaterials[[0, 4, 2][i]].material,
    );
    const pos = at(-half + FRONTAGE / 6 + (i * FRONTAGE) / 3, face + 0.8, 3.82);
    sign.position.set(pos.x, pos.y, pos.z);
    sign.rotation.y = yaw + Math.PI / 2;
    level.root.add(sign);
  }

  /* ---- Collision ------------------------------------------------------ */

  const centre = at(0, 0.3, 0);
  addStaticCollider(level, {
    x: centre.x,
    y: GROUND / 2,
    z: centre.z,
    width: DEPTH + 0.6,
    height: GROUND,
    depth: FRONTAGE + 0.6,
    yaw,
  });
  addStaticCollider(level, {
    x: centre.x,
    y: BODY / 2,
    z: centre.z,
    width: DEPTH,
    height: BODY,
    depth: FRONTAGE,
    yaw,
  });
}

/**
 * LINAA TOWER (ليناء), Street 60 at 15.564274, 32.578146.
 *
 * A wedge of a building on the west side: the south elevation is a stack of
 * rounded white balcony slabs with orange glazing between them, the street
 * corner carries a big orange sail that tapers to a point at the bottom, and a
 * deep red block with a stepped parapet closes the north end. It stands about
 * 7 m behind the pavement behind a low forecourt wall, which is why the
 * frontage sits further back than its neighbours.
 */
function buildLinaaTower(ctx, signMaterials) {
  const { route, level } = ctx;
  const S = 3183;

  const FRONTAGE = 20;
  const DEPTH = 24;
  const GROUND = 4.6;
  const FLOORS = 7;
  const FLOOR = 3.1;
  const BODY = GROUND + FLOORS * FLOOR;
  const PARAPET = 1.0;

  const WHITE = '#efeae0';
  const WHITE_DIM = '#ded7c9';
  const ORANGE = '#e8871f';
  const RED = '#ae2a1d';
  const RED_DEEP = '#8d2015';
  const GLASS = '#252c30';
  const SHADOW = '#b9b1a2';

  // The front face sits 20 m off the centreline — the OSM footprint's nearest
  // corner — which leaves a forecourt between it and the back of the pavement.
  const frontLateral = 20;
  const frame = route.offsetFrame(S, -(frontLateral + DEPTH / 2));
  const { right, tangent, yaw } = frame;
  const at = (along, lateral, y) =>
    new Vector3(
      frame.position.x + tangent.x * along + right.x * lateral,
      y,
      frame.position.z + tangent.z * along + right.z * lateral,
    );
  const slab = (along, lateral, y0, latW, h, alongL, color) =>
    addPart(ctx, -1, S, 'box', {
      position: at(along, lateral, y0 + h / 2),
      scale: new Vector3(latW, h, alongL),
      yaw,
      color,
    });

  const half = FRONTAGE / 2;
  const face = DEPTH / 2;

  /* ---- Mass and forecourt --------------------------------------------- */

  slab(0, 0, 0, DEPTH, BODY, FRONTAGE, WHITE);

  // The forecourt wall, with a gate left open on the centre line.
  for (const along of [-9.5, 9.5]) {
    slab(along, face + 6.4, 0, 0.35, 1.15, 11, '#cbbfa8');
  }

  /* ---- South elevation: rounded slabs with orange between -------------- */

  for (let f = 0; f < FLOORS; f += 1) {
    const base = GROUND + f * FLOOR;

    // The orange glazing sits behind the slabs, so it is drawn first.
    const infill = f === 2 || f === 3 ? RED_DEEP : ORANGE;
    slab(-half - 0.06, 0, base + 0.7, DEPTH - 0.6, FLOOR - 0.7, 0.12, infill);

    slab(-half - 0.75, 0, base, DEPTH + 1.2, 0.7, 1.5, WHITE);

    // The slab edge is a bullnose, which is the building's signature from the
    // road. A cylinder laid along the lateral axis gives that in one primitive.
    addPart(ctx, -1, S, 'cylinder', {
      position: at(-half - 1.5, 0, base + 0.35),
      quaternion: new Quaternion().setFromUnitVectors(UP, right),
      scale: new Vector3(0.35, DEPTH + 1.2, 0.35),
      color: WHITE,
    });
  }

  /* ---- Street facade: the orange sail at the south corner -------------- */

  const sailBottom = 1.6;
  const slices = 52;
  const sliceH = (BODY - sailBottom) / slices;
  for (let i = 0; i < slices; i += 1) {
    const t = i / (slices - 1);
    const width = 0.45 + t * 5.15;
    slab(-half + width / 2, face + 0.1, sailBottom + i * sliceH, 0.14, sliceH, width, ORANGE);
  }

  /* ---- White face with square windows --------------------------------- */

  const columns = [-3.4, -1.4, 0.6, 2.6];
  const jitter = [
    [[0.2, 0.16], [-0.14, -0.2], [0.1, 0.05], [-0.22, 0.18]],
    [[-0.18, 0.1], [0.22, -0.16], [-0.1, 0.2], [0.14, -0.08]],
    [[0.1, -0.18], [-0.2, 0.14], [0.22, -0.06], [-0.08, 0.16]],
  ];
  for (let f = 0; f < FLOORS; f += 1) {
    const base = GROUND + f * FLOOR;
    for (const [i, along] of columns.entries()) {
      const [dx, dy] = jitter[f % 3][i];
      slab(along + dx, face + 0.07, base + 1.0 + dy, 0.06, 0.85, 0.85, GLASS);
    }
  }

  /* ---- Red block at the north end ------------------------------------- */

  slab(7.55, face + 0.6, GROUND, 1.2, BODY + 2.2 - GROUND, 4.7, RED);
  slab(4.75, face + 0.45, GROUND, 0.9, BODY + 0.8 - GROUND, 0.9, RED);
  for (let f = 0; f < FLOORS; f += 1) {
    const base = GROUND + f * FLOOR;
    slab(7.55, face + 1.24, base, 0.06, 0.42, 4.7, RED_DEEP);
    for (const along of [6.6, 8.7]) {
      slab(along, face + 1.26, base + 1.05, 0.06, 0.8, 0.8, GLASS);
    }
  }
  slab(7.55, face + 0.6, BODY + 2.2, 1.4, 0.24, 4.9, RED_DEEP);
  slab(4.75, face + 0.45, BODY + 0.8, 1.1, 0.22, 1.1, RED_DEEP);

  /* ---- Roof, plant and the sign ---------------------------------------- */

  slab(0, 0, BODY, DEPTH + 0.4, PARAPET, FRONTAGE, WHITE);
  slab(0, 0, BODY + PARAPET, DEPTH + 0.5, 0.14, FRONTAGE + 0.4, SHADOW);
  slab(-2.4, -3.0, BODY, 4.2, 2.4, 3.2, WHITE_DIM);
  addPart(ctx, -1, S, 'cylinder', {
    position: at(4.0, -6.5, BODY + 1.3),
    scale: new Vector3(1.15, 2.6, 1.15),
    color: '#8d9aa0',
  });

  // The board is a tall panel on a frame, leaning back a little the way the
  // real one does. It overhangs the parapet so the roof edge cannot cut across
  // the English line from a low camera.
  const tilt = 0.09;
  const panelCentre = at(-3.6, face + 0.3, BODY + PARAPET + 3.6);
  const panelQuat = new Quaternion()
    .setFromAxisAngle(UP, yaw)
    .multiply(new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), tilt));
  addPart(ctx, -1, S, 'box', {
    position: panelCentre,
    quaternion: panelQuat,
    scale: new Vector3(0.35, 7.4, 6.6),
    color: '#f3f1ea',
  });
  for (const along of [-7.0, -0.2]) {
    for (const lateral of [face - 0.6, face + 0.1]) {
      slab(along, lateral, BODY + PARAPET, 0.18, 2.6, 0.18, SHADOW);
    }
  }

  const boardNormal = new Vector3(1, 0, 0).applyQuaternion(panelQuat);
  const board = new Mesh(
    new PlaneGeometry(6.4, 7.2),
    new MeshBasicMaterial({ map: createLinaaSignTexture(), toneMapped: false }),
  );
  board.name = 'linaa-sign';
  board.position.copy(panelCentre).addScaledVector(boardNormal, 0.2);
  board.quaternion.setFromUnitVectors(new Vector3(0, 0, 1), boardNormal);
  level.root.add(board);

  /* ---- Ground floor ---------------------------------------------------- */

  slab(0, face + 0.06, 0.35, 0.12, 3.5, FRONTAGE - 0.8, GLASS);
  for (const along of [-8.6, -4.6, -0.6, 3.4, 7.4]) {
    slab(along, face + 0.32, 0, 0.5, GROUND, 0.5, WHITE);
  }
  slab(-4.2, face + 1.3, 3.7, 2.4, 0.28, 6.4, WHITE_DIM);

  const shopBoard = new Mesh(
    new PlaneGeometry(5.0, 0.75),
    signMaterials[2].material,
  );
  const boardPos = at(-4.2, face + 0.5, 3.15);
  shopBoard.position.set(boardPos.x, boardPos.y, boardPos.z);
  shopBoard.rotation.y = yaw + Math.PI / 2;
  level.root.add(shopBoard);

  /* ---- Collision -------------------------------------------------------- */

  const centre = at(0, 0, 0);
  addStaticCollider(level, {
    x: centre.x,
    y: BODY / 2,
    z: centre.z,
    width: DEPTH,
    height: BODY,
    depth: FRONTAGE,
    yaw,
  });
  const red = at(7.2, face + 0.6, 0);
  addStaticCollider(level, {
    x: red.x,
    y: (BODY + 1.8) / 2,
    z: red.z,
    width: 1.2,
    height: BODY + 1.8,
    depth: 5.4,
    yaw,
  });
}

function addPart(
  ctx,
  side,
  s,
  shape,
  { position, scale, yaw = 0, quaternion = null, color },
) {
  const source = SOURCES[shape];
  if (!source) return;

  const geometry = source.clone();
  const rotation = quaternion ?? new Quaternion().setFromAxisAngle(UP, yaw);
  const matrix = new Matrix4().compose(position, rotation, scale);
  geometry.applyMatrix4(matrix);
  addColoredChunkGeometry(ctx, side, s, geometry, color);
}

function addColoredChunkGeometry(ctx, side, s, geometry, color) {
  let prepared = geometry;
  if (prepared.index) {
    const indexed = prepared;
    prepared = prepared.toNonIndexed();
    indexed.dispose();
  }

  const key = `${side}:${Math.floor(s / 220)}`;
  const count = prepared.attributes.position.count;
  const colors = new Float32Array(count * 3);
  const parsed = new Color(color);
  for (let i = 0; i < count; i += 1) {
    colors[i * 3] = parsed.r;
    colors[i * 3 + 1] = parsed.g;
    colors[i * 3 + 2] = parsed.b;
  }
  prepared.setAttribute('color', new Float32BufferAttribute(colors, 3));

  let list = ctx.chunks.get(key);
  if (!list) {
    list = [];
    ctx.chunks.set(key, list);
  }
  list.push(prepared);
}

/**
 * A low-poly wrecked car built from the same merged city primitives. Parts are
 * transformed by the car's full yaw/roll pose, so some cars can rest on their
 * side or roof inside the pile.
 */
function addWreckCar(ctx, side, s, {
  position,
  yaw,
  roll = 0,
  color,
  random,
}) {
  const carRotation = new Quaternion().setFromAxisAngle(UP, yaw);
  if (roll) {
    carRotation.multiply(
      new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), roll),
    );
  }
  const carMatrix = new Matrix4().compose(position, carRotation, new Vector3(1, 1, 1));

  const addPiece = (
    shape,
    localPosition,
    localScale,
    localQuaternion = IDENTITY_QUAT,
    pieceColor = color,
  ) => {
    const localMatrix = new Matrix4().compose(
      new Vector3(...localPosition),
      localQuaternion,
      new Vector3(...localScale),
    );
    const worldMatrix = new Matrix4().multiplyMatrices(carMatrix, localMatrix);
    const worldPosition = new Vector3();
    const worldQuaternion = new Quaternion();
    const worldScale = new Vector3();
    worldMatrix.decompose(worldPosition, worldQuaternion, worldScale);
    addPart(ctx, side, s, shape, {
      position: worldPosition,
      quaternion: worldQuaternion,
      scale: worldScale,
      color: pieceColor,
    });
  };

  addPiece('box', [0, 0.36, 0], [1.58, 0.2, 3.58], IDENTITY_QUAT, WRECK_DARK);
  addPiece('box', [0, 0.58, 0], [1.74, 0.42, 3.95]);
  addPiece('box', [0, 0.84, 1.31], [1.58, 0.14, 0.92]);
  addPiece('box', [0, 0.88, -1.49], [1.6, 0.22, 0.75]);
  addPiece('box', [0, 1.0, -0.2], [1.46, 0.5, 1.52], IDENTITY_QUAT, WRECK_GLASS);
  addPiece(
    'box',
    [0, 1.27, -0.2],
    [1.34, 0.1, 1.3],
    new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), random.range(-0.07, 0.07)),
  );
  addPiece('box', [0, 0.43, 1.99], [1.82, 0.14, 0.13], IDENTITY_QUAT, WRECK_DARK);
  addPiece('box', [0, 0.43, -1.99], [1.82, 0.14, 0.13], IDENTITY_QUAT, WRECK_DARK);

  const missingWheel = random.int(0, WHEEL_LOCAL_POSITIONS.length - 1);
  WHEEL_LOCAL_POSITIONS.forEach((wheel, index) => {
    if (index === missingWheel) return;
    addPiece('cylinder', wheel, [0.33, 0.22, 0.33], WHEEL_QUAT, WRECK_RUBBER);
  });
}

/**
 * Fill an opening in a roadside wall with a small wreck pile. Cross-street
 * mouths get a three-car barricade; compound, mosque and school gates get two
 * cars. Everything is merged into the normal city chunks, so the wrecks add no
 * extra draw calls. Station driveways deliberately stay clear.
 */
function addWreckPile(ctx, side, s, lateral, style) {
  const { route, wreckRandom: random } = ctx;
  const isCross = style === 'cross';
  const carCount = isCross ? 3 : 2;
  const spacing = isCross ? 4.0 : 1.65;
  const scatter = isCross ? 0.7 : 0.25;
  const frame = route.frameAt(s);
  const scorchFrame = route.offsetFrame(s, lateral);

  addPart(ctx, side, s, 'box', {
    position: new Vector3(scorchFrame.position.x, 0.024, scorchFrame.position.z),
    scale: new Vector3(isCross ? 6.2 : 3.8, 0.012, isCross ? 4.7 : 4.1),
    yaw: frame.yaw + random.range(-0.1, 0.1),
    color: WRECK_SCORCH,
  });

  for (let i = 0; i < carCount; i += 1) {
    const carS = s + (i - (carCount - 1) / 2) * spacing + random.range(-0.3, 0.3);
    const carLateral = lateral
      + random.range(-scatter, scatter)
      + (isCross ? 0 : side * (i === 0 ? 0 : 1.1));
    const carFrame = route.offsetFrame(carS, carLateral);
    const pose = random();
    let roll = 0;
    let rootY = 0.02;
    if (pose < 0.18) {
      roll = (random() < 0.5 ? -1 : 1) * Math.PI / 2;
      rootY = 0.88;
    } else if (pose < 0.27) {
      roll = Math.PI;
      rootY = 1.28;
    }

    addWreckCar(ctx, side, carS, {
      position: new Vector3(carFrame.position.x, rootY, carFrame.position.z),
      yaw: carFrame.yaw + random.range(-0.32, 0.32),
      roll,
      color: random.pick(WRECK_BODY_COLORS),
      random,
    });
    ctx.wreckCars += 1;
  }

  for (let i = 0; i < 5; i += 1) {
    const debrisFrame = route.offsetFrame(
      s + random.range(isCross ? -4.5 : -1.8, isCross ? 4.5 : 1.8),
      lateral + random.range(-1.8, 1.8),
    );
    addPart(ctx, side, s, 'box', {
      position: new Vector3(
        debrisFrame.position.x,
        random.range(0.03, 0.16),
        debrisFrame.position.z,
      ),
      scale: new Vector3(
        random.range(0.15, 0.5),
        random.range(0.04, 0.14),
        random.range(0.2, 0.65),
      ),
      yaw: random() * Math.PI * 2,
      color: random.pick(['#32302b', '#4c4a40', '#6a6253', '#222524']),
    });
  }

  for (let i = 0; i < 2; i += 1) {
    const tyreFrame = route.offsetFrame(
      s + random.range(isCross ? -3.5 : -1.2, isCross ? 3.5 : 1.2),
      lateral + random.range(-1.4, 1.4),
    );
    addPart(ctx, side, s, 'cylinder', {
      position: new Vector3(tyreFrame.position.x, 0.12, tyreFrame.position.z),
      scale: new Vector3(0.33, 0.15, 0.33),
      yaw: random() * Math.PI,
      color: WRECK_RUBBER,
    });
  }

  ctx.wreckPiles += 1;
}

function flushChunks(ctx, material) {
  for (const [key, geometries] of ctx.chunks) {
    if (!geometries.length) continue;
    const merged = mergeGeometries(geometries, false);
    for (const geometry of geometries) geometry.dispose();
    if (!merged) continue;

    const mesh = new Mesh(merged, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = `city-chunk-${key}`;
    ctx.level.root.add(mesh);
  }
  ctx.chunks.clear();
}

function rootAdd(level, object) {
  level.root.add(object);
}

function blockModulo(value, length) {
  return Math.abs(Math.floor(value / 100)) % length;
}
